import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle,
  RefreshCw,
  MapPin,
  QrCode,
  Bell,
  Camera,
  Timer,
  AlertTriangle,
  User,
  Target,
  Package
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import './WorkerDashboard.css';

const DEFAULT_STATS = {
  completed: 0,
  pending: 0,
  urgent: 0
};

const WorkerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Essential states only
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 300000); // Update every 5 minutes
    return () => clearInterval(interval);
  }, [user?.id]); // Only re-run if user ID changes

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('binbuddy_token');
      
      const userId = user?.id || user?._id;
      if (!token || !userId) {
        setLoading(false);
        return;
      }

      // Fetch assigned tasks and notifications in parallel
      
      const [tasksRes, notificationsRes, regularTasksRes] = await Promise.all([
        // Enhanced tasks (report-based)
        axios.get(`/api/tasks-enhanced/worker/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          return res;
        }).catch(err => {
          console.error('Enhanced Tasks API error:', err.response?.status, err.response?.data || err.message);
          return { data: { tasks: [] } };
        }),
        // Notifications
        axios.get(`/api/notifications-enhanced/worker/${userId}?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          return res;
        }).catch(err => {
          console.error('Notifications API error:', err.response?.status, err.response?.data || err.message);
          return { data: { notifications: [], unreadCount: 0 } };
        }),
        // Regular tasks
        axios.get(`/api/tasks/worker/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => {
          return res;
        }).catch(err => {
          console.error('Regular tasks API error:', err.response?.status, err.response?.data || err.message);
          return { data: [] };
        })
      ]);

      // Combine enhanced tasks and regular tasks
      const enhancedTasks = tasksRes.data.tasks || [];
      const regularTasks = Array.isArray(regularTasksRes.data) ? regularTasksRes.data : [];
      
      // Process and combine all tasks
      const processedEnhancedTasks = enhancedTasks.map(task => ({
        ...task,
        title: task.title || 'Enhanced Task',
        description: task.description || 'Task assigned through enhanced system',
        deadline: task.deadline || task.schedule?.endTime || new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        bins: task.bins || [],
        taskType: task.taskType || 'report_based',
        priority: task.priority || 'medium',
        status: task.status || 'assigned'
      }));

      const processedRegularTasks = regularTasks.map(task => ({
        ...task,
        title: task.title || 'Regular Task',
        description: task.description || 'Scheduled collection task',
        deadline: task.schedule?.endTime || task.deadline || new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
        bins: task.bins || [],
        taskType: 'regular',
        priority: task.priority || 'medium',
        status: task.status || 'assigned'
      }));

      const allTasks = [...processedEnhancedTasks, ...processedRegularTasks];
      
      // Remove duplicates by creating a Map with unique IDs
      const taskMap = new Map();
      
      // Add all real tasks to the map
      allTasks.forEach(task => {
        if (task._id) {
          taskMap.set(task._id.toString(), task);
        }
      });

      // Convert map back to array with unique tasks
      const finalTasks = Array.from(taskMap.values());
      
      setAssignedTasks(finalTasks);
      setNotifications(notificationsRes.data.notifications || []);
      setUnreadCount(notificationsRes.data.unreadCount || 0);
      
      // Update stats based on tasks
      const completedTasks = finalTasks.filter(task => task.status === 'completed').length;
      const pendingTasks = finalTasks.filter(task => ['assigned', 'inProgress'].includes(task.status)).length;
      const urgentTasks = finalTasks.filter(task => {
        const timeLeft = new Date(task.deadline) - new Date();
        return timeLeft <= 15 * 60 * 1000 && timeLeft > 0; // 15 minutes or less
      }).length;
      
      setStats({
        completed: completedTasks,
        pending: pendingTasks,
        urgent: urgentTasks
      });

      setLastUpdate(new Date());
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch dashboard data';
      setError(errorMessage);
      console.error('Dashboard fetch error:', err);
      toast.error(`Error loading dashboard: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };


  // Handle task actions (memoized for performance)
  const handleStartTask = useCallback((taskId) => {
    // Navigate to QR Scanner with task verification mode
    navigate(`/qr-scanner?taskId=${taskId}`);
  }, [navigate]);

  const handleViewTaskDetails = (task) => {
    // Show task details modal or navigate to task details page
    // console.log('View task details:', task);
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('binbuddy_token');
      await axios.patch(`/api/notifications-enhanced/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(notif => 
          notif._id === notificationId ? { ...notif, read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };


  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffMs = deadlineDate - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 60) return `${diffMins}m left`;
    
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m left`;
  };

  const getTaskPriorityColor = useCallback((task) => {
    if (task.taskType === 'report_based') {
      const timeLeft = new Date(task.deadline) - new Date();
      if (timeLeft <= 5 * 60 * 1000) return 'urgent'; // 5 minutes left
      if (timeLeft <= 15 * 60 * 1000) return 'high'; // 15 minutes left
    }
    return task.priority || 'medium';
  }, []);




  return (
    <div className="worker-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="worker-avatar">
          <User size={24} />
        </div>
        <div>
          <h1>Hello, {user?.name || 'Worker'}!</h1>
          <p className="worker-role">Field Worker</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            onClick={fetchDashboardData} 
            className="refresh-btn" 
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card completed">
          <div className="stat-content">
            <div className="stat-number">{stats.completed}</div>
            <div className="stat-label">Completed</div>
          </div>
          <CheckCircle className="stat-icon" size={24} />
        </div>
        
        <div className="stat-card pending">
          <div className="stat-content">
            <div className="stat-number">{stats.pending}</div>
            <div className="stat-label">Pending</div>
          </div>
          <Clock className="stat-icon" size={24} />
        </div>
        
        <div className="stat-card urgent">
          <div className="stat-content">
            <div className="stat-number">{stats.urgent}</div>
            <div className="stat-label">Urgent</div>
          </div>
          <AlertTriangle className="stat-icon" size={24} />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="actions-section">
        <button 
          className="action-btn primary"
          onClick={() => navigate('/qr-scanner')}
        >
          <QrCode size={20} />
          <span>Scan QR Code</span>
        </button>
        <button 
          className="action-btn secondary"
          onClick={() => navigate('/nearby-bins')}
        >
          <MapPin size={20} />
          <span>View Map</span>
        </button>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="section">
          <div className="section-title">
            <Bell size={18} />
            <span>Notifications</span>
            {unreadCount > 0 && <div className="notification-badge">{unreadCount}</div>}
          </div>
          <div className="notifications-container">
            {notifications.slice(0, 3).map(notification => (
              <div 
                key={notification._id} 
                className={`notification-card ${!notification.read ? 'unread' : ''}`}
                onClick={() => !notification.read && markNotificationAsRead(notification._id)}
              >
                <div className="notification-text">
                  <div className="notification-title">{notification.title}</div>
                  <div className="notification-message">{notification.message}</div>
                  <div className="notification-time">
                    {new Date(notification.createdAt).toLocaleString()}
                  </div>
                </div>
                {!notification.read && <div className="unread-indicator"></div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks Section */}
      <div className="section">
        <div className="section-title">
          <Package size={18} />
          <span>Your Tasks ({assignedTasks.length})</span>
        </div>
        <div className="tasks-container">
          {loading ? (
            <div className="loading-message">
              <RefreshCw className="spinning" size={20} />
              <span>Loading tasks...</span>
            </div>
          ) : assignedTasks.length === 0 ? (
            <div className="empty-message">
              <CheckCircle size={32} />
              <div className="empty-text">
                <h3>No Tasks Assigned</h3>
                <p>You're all caught up! Check back later for new assignments.</p>
              </div>
            </div>
          ) : (
            assignedTasks.map((task, index) => (
              <div key={task._id || `task-${index}`} className={`task-card priority-${getTaskPriorityColor(task)}`}>
                <div className="task-main">
                  <div className="task-info">
                    <div className="task-title">
                      {task.title || 'Task Assignment'}
                    </div>
                    <div className="task-description">{task.description || 'Complete assigned work'}</div>
                    <div className="task-meta">
                      <div className="task-location">
                        <MapPin size={14} />
                        <span>{task.bins?.[0]?.address || task.bins?.[0]?.location?.address || 'Location not specified'}</span>
                      </div>
                      <div className="task-deadline">
                        <Timer size={14} />
                        <span className={new Date(task.deadline) <= new Date() ? 'expired' : ''}>
                          {getTimeRemaining(task.deadline)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="task-priority">
                    {task.taskType === 'report_based' ? 'Report' : 'Regular'}
                  </div>
                </div>
                
                <div className="task-actions">
                  {task.status === 'assigned' && (
                    <button 
                      className="task-btn start"
                      onClick={() => handleStartTask(task._id)}
                    >
                      <QrCode size={16} />
                      Start Task
                    </button>
                  )}
                  {task.status === 'inProgress' && (
                    <button 
                      className="task-btn continue"
                      onClick={() => handleStartTask(task._id)}
                    >
                      <Camera size={16} />
                      Continue
                    </button>
                  )}
                  {task.status === 'verification_pending' && (
                    <div className="task-status pending">
                      <Clock size={16} />
                      Under Review
                    </div>
                  )}
                  {task.status === 'completed' && (
                    <div className="task-status completed">
                      <CheckCircle size={16} />
                      Completed
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerDashboard; 